/**
 * LINE Login Callback Page
 * Handles the redirect from LINE OAuth → exchanges code for Firebase Custom Token
 * Flow: LINE redirect → this page → exchange code via tRPC → Firebase signIn → redirect to app
 */
import { useEffect, useState, useRef } from "react";
import { useLocation, useSearch } from "wouter";
import { trpc } from "@/lib/trpc";
import { firebaseSignInWithCustomToken } from "@/lib/firebase";
import Layout from "@/components/Layout";

export default function LineCallback() {
  const [, setLocation] = useLocation();
  const search = useSearch();
  const [status, setStatus] = useState<"loading" | "success" | "error">("loading");
  const [errorMessage, setErrorMessage] = useState("");
  const exchangeRef = useRef(false);

  const exchangeCode = trpc.firebase.exchangeLineCode.useMutation();

  useEffect(() => {
    // Prevent double execution in React StrictMode
    if (exchangeRef.current) return;
    exchangeRef.current = true;

    const params = new URLSearchParams(search);
    const code = params.get("code");
    const returnPath = params.get("returnPath") || "/";

    if (!code) {
      setStatus("error");
      setErrorMessage("ไม่พบ authorization code จาก LINE");
      return;
    }

    // Exchange LINE code for Firebase Custom Token
    const redirectUri = `${window.location.origin}/api/auth/line/callback`;

    exchangeCode.mutateAsync({ code, redirectUri })
      .then(async (result: any) => {
        // Sign in to Firebase with custom token
        await firebaseSignInWithCustomToken(result.customToken);
        setStatus("success");

        // Redirect after short delay
        setTimeout(() => {
          setLocation(returnPath);
        }, 1000);
      })
      .catch((error: any) => {
        console.error("[LINE Callback] Exchange failed:", error);
        setStatus("error");
        setErrorMessage(error.message || "เกิดข้อผิดพลาดในการเข้าสู่ระบบ");
      });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <Layout>
      <section className="bg-black text-white min-h-[60vh] flex items-center justify-center">
        <div className="container text-center py-20">
          {status === "loading" && (
            <div>
              <div className="inline-block w-16 h-16 border-4 border-white/20 border-t-white rounded-full animate-spin mb-6" />
              <h2 className="font-heading font-bold text-2xl md:text-3xl tracking-tight mb-4">
                กำลังเข้าสู่ระบบ...
              </h2>
              <p className="text-white/60 font-mono text-sm">
                กำลังยืนยันตัวตนกับ LINE
              </p>
            </div>
          )}

          {status === "success" && (
            <div>
              <div className="inline-flex items-center justify-center w-16 h-16 border-3 border-white bg-white text-black mb-6">
                <svg className="w-8 h-8" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <h2 className="font-heading font-bold text-2xl md:text-3xl tracking-tight mb-4">
                เข้าสู่ระบบสำเร็จ!
              </h2>
              <p className="text-white/60 font-mono text-sm">
                กำลังนำคุณไปยังหน้าหลัก...
              </p>
            </div>
          )}

          {status === "error" && (
            <div>
              <div className="inline-flex items-center justify-center w-16 h-16 border-3 border-white mb-6">
                <svg className="w-8 h-8" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </div>
              <h2 className="font-heading font-bold text-2xl md:text-3xl tracking-tight mb-4">
                เข้าสู่ระบบไม่สำเร็จ
              </h2>
              <p className="text-white/60 font-mono text-sm mb-6">
                {errorMessage}
              </p>
              <button
                onClick={() => setLocation("/")}
                className="btn-brutal bg-white text-black"
              >
                กลับหน้าแรก
              </button>
            </div>
          )}
        </div>
      </section>
    </Layout>
  );
}
